import { Edit, PackagePlus, Plus, RotateCcw } from 'lucide-react';
import { formatPrice, useTranslation } from '@dukanti/shared';
import type { BarcodeLookupResult, TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';

interface BarcodeLookupResultPanelProps {
  readonly result: BarcodeLookupResult | null;
  readonly onEditProduct: (productId: string) => void;
  readonly onCreateProduct?: (barcode: string, deletedReuseConfirmedNormalized?: string) => void;
  readonly onUpdateStock?: () => void;
  readonly onScanAnother: () => void;
}

function BarcodeValue({ value }: { readonly value: string }): React.JSX.Element {
  return <bdi dir="ltr">{value}</bdi>;
}

export function BarcodeLookupResultPanel({
  result,
  onEditProduct,
  onCreateProduct,
  onUpdateStock,
  onScanAnother,
}: BarcodeLookupResultPanelProps): React.JSX.Element | null {
  const { t, lang } = useTranslation();
  if (!result) return null;

  if (result.status === 'invalid') {
    return (
      <section
        role="alert"
        className="border-destructive/25 bg-destructive/10 text-destructive rounded-lg border p-4 text-start"
        data-testid="barcode-invalid-result"
      >
        <h2 className="font-semibold">{t('admin.barcode.result.invalidTitle')}</h2>
        <p className="mt-1 text-sm">{t(result.errorKey as TranslationKey)}</p>
      </section>
    );
  }

  if (result.status === 'unknown') {
    return (
      <section
        className="border-border bg-card shadow-admin-card rounded-lg border p-4 text-start"
        aria-live="polite"
        data-testid="barcode-unknown-result"
      >
        <h2 className="font-display text-foreground text-xl">
          {t('admin.barcode.result.unknownTitle')}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          <BarcodeValue value={result.normalized} />
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => onCreateProduct?.(result.normalized)}
            data-testid="barcode-create-product"
          >
            <Plus className="me-2 size-4" aria-hidden />
            {t('admin.barcode.result.createProduct')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onScanAnother}
            data-testid="barcode-scan-another"
          >
            {t('admin.barcode.scan.scanAnother')}
          </Button>
        </div>
      </section>
    );
  }

  if (result.status === 'deleted_recoverable') {
    return (
      <section
        className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-start text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100"
        aria-live="polite"
        data-testid="barcode-deleted-recoverable"
      >
        <h2 className="font-display text-foreground text-xl">
          {t('admin.barcode.result.deletedTitle')}
        </h2>
        <p className="text-muted-foreground mt-1 text-sm">
          {result.deleted.productName} · <BarcodeValue value={result.deleted.barcodeValue} />
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            type="button"
            onClick={() => onCreateProduct?.(result.deleted.barcodeValue, result.normalized)}
          >
            <RotateCcw className="me-2 size-4" aria-hidden />
            {t('admin.barcode.result.reuseDeleted')}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onScanAnother}
            data-testid="barcode-scan-another"
          >
            {t('admin.barcode.scan.scanAnother')}
          </Button>
        </div>
      </section>
    );
  }

  const variant = result.variant;
  return (
    <section
      className="border-border bg-card shadow-admin-card rounded-lg border p-4 text-start"
      aria-live="polite"
      data-testid="barcode-lookup-result"
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-foreground text-xl">
            {t('admin.barcode.result.knownTitle')}
          </h2>
          <p className="text-foreground mt-1 font-medium">{variant.productName}</p>
          <p className="text-muted-foreground mt-1 text-sm">
            <BarcodeValue value={variant.barcodeValue} />
          </p>
        </div>
        <span
          className="border-border bg-admin-surface-muted rounded-full border px-3 py-1 text-xs"
          data-testid="barcode-variant-status"
        >
          {t(
            `admin.barcode.result.status.${variant.availabilityState === 'out_of_stock' ? 'outOfStock' : variant.active ? 'active' : 'disabled'}` as TranslationKey,
          )}
        </span>
      </div>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.barcode.result.variant')}</dt>
          <dd className="text-foreground text-sm">{variant.variantOptionSummary || '-'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.barcode.result.category')}</dt>
          <dd className="text-foreground text-sm">{variant.categoryName ?? '-'}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.barcode.result.price')}</dt>
          <dd className="text-foreground text-sm">
            {formatPrice(variant.salePriceCents ?? variant.priceCents, lang)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground text-xs">{t('admin.barcode.result.stock')}</dt>
          <dd className="text-foreground text-sm" dir="ltr" data-testid="barcode-current-stock">
            {variant.inventory}
          </dd>
        </div>
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button type="button" onClick={onUpdateStock} data-testid="barcode-stock-update-open">
          <PackagePlus className="me-2 size-4" aria-hidden />
          {t('admin.barcode.result.updateStock')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => onEditProduct(variant.productId)}
          data-testid="barcode-edit-product"
        >
          <Edit className="me-2 size-4" aria-hidden />
          {t('admin.barcode.result.editProduct')}
        </Button>
        <Button
          type="button"
          variant="ghost"
          onClick={onScanAnother}
          data-testid="barcode-scan-another"
        >
          {t('admin.barcode.scan.scanAnother')}
        </Button>
      </div>
    </section>
  );
}
